import type {
	ShadowEventMeasurement,
	ShadowEventSummary,
} from "@sitescene/types";

import { Link, useParams } from "react-router";

import { Badge } from "@/components/badge";
import { ClipPanel } from "@/components/clip-panel";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { Button } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { plausibilityVerificationPath, ROUTES } from "@/constants/routes";
import {
	isNotFoundError,
	useEventReanalysisView,
	useShadowEvent,
	useShadowEventVideo,
} from "@/features/reanalysis/use-reanalysis";
import { apiErrorMessage } from "@/features/scene-facts/use-scene-proposals";
import { formatWhen } from "@/pages/plausibility/format";

import { CorrectionPanel } from "./CorrectionPanel";
import { ReanalysisHistory } from "./ReanalysisHistory";

/**
 * One event, laid out for correcting it: what it says now and the clip it
 * came from, the correction panel, then every reanalysis of it, newest
 * first, with the evidence each run weighed.
 */
export const ReanalysisEvent = () => {
	const { eventId } = useParams();
	const { data, error, isLoading } = useShadowEvent(eventId);

	return (
		<div className="flex flex-col gap-4">
			<Link
				className="text-secondary text-sm underline"
				to={ROUTES.ORGANIZATION_REANALYSIS}
			>
				← All reanalyses
			</Link>
			{isLoading || !data ? (
				<StatusMessage
					empty={`No event ${eventId ?? ""} in this organization.`}
					error={isNotFoundError(error) ? null : error}
					isEmpty={!isLoading && !data}
					isLoading={isLoading}
					loading="Loading event…"
				/>
			) : (
				<>
					<div className="grid gap-4 lg:grid-cols-2">
						<EventSummary event={data} />
						<EventClip event={data} />
					</div>
					<Reanalysis eventId={data.id} />
				</>
			)}
		</div>
	);
};

const EventSummary = ({ event: e }: { event: ShadowEventSummary }) => {
	const columns: Column<ShadowEventMeasurement>[] = [
		{
			header: "Detection",
			key: "type",
			render: (m) => <code className="text-xs">{m.type}</code>,
		},
		{ header: "Severity", key: "severity", render: (m) => String(m.severity) },
		{ header: "Clip", key: "clip", render: (m) => (m.hasClip ? "Yes" : "No") },
		{
			header: "Verification",
			key: "verification",
			render: (m) =>
				m.verification ? (
					<span className="flex flex-wrap items-center gap-2">
						<Link
							className="underline"
							to={plausibilityVerificationPath(m.verification.id)}
						>
							Open verification
						</Link>
						<Badge value={m.verification.decision ?? m.verification.status} />
					</span>
				) : (
					<span className="text-tertiary">—</span>
				),
		},
	];

	return (
		<DetailPanel title="Event">
			<KeyValueList
				items={[
					{ label: "Title", value: e.title ?? "—" },
					{ label: "Description", value: e.description || "—" },
					{ label: "Severity", value: String(e.severity) },
					{ label: "Types", value: e.types.join(", ") || "—" },
					{ label: "Status", value: <Badge value={e.status} /> },
					{
						label: "Time",
						value: `${formatWhen(e.startTimestamp)} – ${formatWhen(e.endTimestamp)}`,
					},
					{ label: "Event ID", value: <code className="text-xs">{e.id}</code> },
				]}
			/>
			<h4 className="text-primary mb-2 mt-4 text-sm font-semibold">
				Detections
			</h4>
			<DataTable columns={columns} rowKey={(m) => m.id} rows={e.measurements} />
		</DetailPanel>
	);
};

/** The clip the reanalysis will read, so staff see it before correcting. */
const EventClip = ({ event }: { event: ShadowEventSummary }) => {
	const hasClip = event.measurements.some((m) => m.hasClip);
	const video = useShadowEventVideo(hasClip ? event.id : undefined);
	return (
		<ClipPanel
			empty="No detection on this event has a clip."
			error={video.error}
			hasClip={hasClip}
			isLoading={video.isLoading}
			mediaKey={video.data?.mediaKey}
			testId="event-clip"
			video={video.data}
		/>
	);
};

/**
 * The correction panel and the history share one read, polled while a run
 * is in flight. Only a first load that failed replaces them: a background
 * refetch that fails keeps what was last loaded, and what staff were typing.
 */
const Reanalysis = ({ eventId }: { eventId: string }) => {
	const { data, dataUpdatedAt, error, isFetching, isLoading, refetch } =
		useEventReanalysisView(eventId);
	const refresh = () => void refetch();

	if (isLoading || !data) {
		return (
			<StatusMessage
				empty="No reanalysis record for this event."
				error={error}
				isEmpty={!isLoading && !data}
				isLoading={isLoading}
				loading="Loading reanalyses…"
			/>
		);
	}

	return (
		<>
			{error && (
				<div
					className="bg-warning-primary text-warning-primary flex flex-wrap items-center gap-3 rounded-md p-3 text-sm"
					role="alert"
				>
					<span>
						Could not refresh the reanalyses: {apiErrorMessage(error)}. Showing
						what was last loaded.
					</span>
					<Button
						disabled={isFetching}
						onClick={refresh}
						type="button"
						variant="secondary"
					>
						Try again
					</Button>
				</div>
			)}
			<CorrectionPanel eventId={eventId} view={data} />
			<ReanalysisHistory
				checkedAt={dataUpdatedAt}
				eventId={eventId}
				isFetching={isFetching}
				view={data}
			/>
		</>
	);
};
